<template>
  <div class="charts-customer-banner" ref="customChartContainerRef">
    <div :id="'banner-' + customerBannerUniqueId" class="carousel slide" data-bs-ride="carousel">
      <template v-if="!loading">
        <template v-if="customers.length > 0">
          <div class="carousel-inner" :class="isShowInfoAble ? 'cursor-pointer' : ''" @dblclick="isShowInfoAble ? $emit('on-click-data-info') : ''">
            <div v-for="(customer, index) in customers" :key="index" class="carousel-item" :class="[{ active: index === 0 }]">
              <div v-if="customer.isVerified" class="verified-icon-container">
                <verified-icon />
              </div>
              <div class="image-container">
                <img
                  v-if="customer.imageUrl && customer.imageUrl != '-'"
                  :src="customer.imageUrl"
                  alt="no img"
                  :style="{
                    width: autoRatioHeightHandler(50, '8.381rem'),
                    height: autoRatioHeightHandler(50, '8.381rem'),
                  }"
                />
                <div
                  v-else
                  class="avatar"
                  :style="{
                    width: autoRatioHeightHandler(50, '8.381rem'),
                    height: autoRatioHeightHandler(50, '8.381rem'),
                    fontSize: autoRatioHeightHandler(150, '3rem'),
                    backgroundColor: '#' + colorBannerIconUniqueId,
                  }"
                >
                  {{ customer.firstname[0] }}
                </div>
              </div>
              <div
                class="title"
                :style="{
                  fontSize: autoRatioHeightHandler(225, '1.938rem'),
                  marginTop: autoRatioHeightHandler(1000, '1rem'),
                }"
              >
                {{ `${customer.firstname || '-'} ${customer.lastname || '-'}` }}
              </div>
              <div class="subtitle" :style="{ fontSize: autoRatioHeightHandler(275, '1.375rem') }">
                {{ `${customer.email || '-'}` }}
              </div>
              <hr :style="{ marginTop: autoRatioHeightHandler(400, '1.5rem'), marginBottom: autoRatioHeightHandler(400, '1.5rem') }" />
              <div class="row">
                <div
                  class="col summary-box"
                  :style="{ fontSize: autoRatioHeightHandler(300, '1.375rem'), marginBottom: autoRatioHeightHandler(1000, '1rem') }"
                >
                  {{ `${customer.leftTitle}` }} <br />
                  {{ `${customer.balancePoint.toLocaleString()}` }}
                </div>
                <div
                  class="col summary-box"
                  :style="{ fontSize: autoRatioHeightHandler(300, '1.375rem'), marginBottom: autoRatioHeightHandler(1000, '1rem') }"
                >
                  {{ `${customer.rightTitle}` }} <br />
                  {{ `${customer.spending.toLocaleString()}` }}
                </div>
              </div>
            </div>
          </div>
          <span class="control-prev icon-container" :data-bs-target="'#banner-' + customerBannerUniqueId" data-bs-slide="prev">
            <prev-icon />
          </span>
          <span class="control-next icon-container" :data-bs-target="'#banner-' + customerBannerUniqueId" data-bs-slide="next">
            <next-icon />
          </span>
        </template>
        <template v-else>
          <div class="no-data">No data</div>
        </template>
      </template>
      <template v-else>
        <div class="loading">
          <loader-spinner />
        </div>
      </template>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, Ref } from 'vue';

import LoaderSpinner from '@/views/components/loader/loader-spinner.vue';

import PrevIcon from '@/assets/icons/modules/analytics/bi/PrevIcon.vue';
import NextIcon from '@/assets/icons/modules/analytics/bi/NextIcon.vue';
import VerifiedIcon from '@/assets/icons/modules/analytics/bi/VerifiedIcon.vue';

import { useElementSize } from '@vueuse/core';

interface Props {
  customers: ChartBanner.Customer[];
  loading: boolean;
  autoRatio?: boolean;
  isShowInfoAble?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  customers: () => [],
  loading: false,
  autoRatio: false,
  isShowInfoAble: false,
});

defineEmits(['on-click-data-info']);

const customChartContainerRef = ref(null);
const { width, height } = useElementSize(customChartContainerRef);

const customerBannerUniqueId: Ref<string> = ref(Math.random().toFixed(8).replace('.', ''));
const colorBannerIconUniqueId: Ref<string> = ref(Math.floor(Math.random() * 16777215).toString(16));

// กันไว้เผื่อ ResizeObserver วัดขนาดผิดพลาดชั่วขณะ (เช่นตอน resize จอ) แล้วค่าค้าง
// ถ้าไม่ clamp ไว้ font/รูปโปรไฟล์จะบวม/ยุบผิดปกติตามค่าที่วัดผิดไปด้วย
const MIN_MEASURED_SIZE_PX = 100;
const MAX_MEASURED_SIZE_PX = 800;
const clampMeasuredSize = (value: number) => Math.min(MAX_MEASURED_SIZE_PX, Math.max(MIN_MEASURED_SIZE_PX, value));

const autoRatioWidthHandler = computed(() => {
  return (divider: number, defaultValue: string) => `${props.autoRatio ? String(clampMeasuredSize(width.value) / divider) + 'rem' : defaultValue}`;
});

const autoRatioHeightHandler = computed(() => {
  return (divider: number, defaultValue: string) => `${props.autoRatio ? String(clampMeasuredSize(height.value) / divider) + 'rem' : defaultValue}`;
});
</script>

<style lang="scss">
@import '@/assets/styles/modules/analytics/bi/analytics-bi-charts-customer-banner.scss';

/* (optional) How to override global carousel icon
1. goto web/cdp-frontend/src/assets/styles/base/carousel.scss
2. adjust color in scss variable ==> $carousel-control-color
3. adjust prev icon by replace SVG tag in scss variable ==> $carousel-control-prev-icon-bg
4. adjust next icon by replace SVG tag in scss variable ==> $carousel-control-next-icon-bg
5. make sure to import carousel.scss file in web/cdp-frontend/src/assets/styles/vendor/bootstrap.scss
6. icon and color must be changed!
*/
</style>
